import { atom } from 'jotai';

import { implog } from '@/api/implog';
import { ACCESS_USERID_KEY, recentlyGameWhiteList } from '@/constant/config';
import { GameItemType } from '@/pages/game/components/gameitem';
import { getGamesUrl } from '@/utils/img';
import { request } from '@/utils/request';
import Storage from '@/utils/Storage';
/** 基础游戏列表 */
export const gamesListAtom = atom<GameItemType[]>([]);
/** 广告游戏列表 */
export const advertisesAtom = atom<GameItemType[]>([]);
/** 推荐游戏列表 */
export const recommendAtom = atom<GameItemType[]>([]);
/** 圣诞主题游戏列表 */
export const christmasAtom = atom<GameItemType[]>([]);
/** 捕鱼类游戏列表 */
export const fishAtom = atom<GameItemType[]>([]);

/** 组合游戏列表（兼容旧结构） */
export const combinedGamesAtom = atom(get => ({
  games: get(gamesListAtom),
  advertises: get(advertisesAtom),
  recommend: get(recommendAtom),
  christmas: get(christmasAtom),
  fish: get(fishAtom)
}));

/** 最近游玩游戏列表 */
export const recentlyGameAtom = atom<GameItemType[]>([]);
/** 格式化后的最近游戏列表 */
export const formattedRecentlyGameAtom = atom(get => {
  const games = get(recentlyGameAtom);
  const gameConfigs = get(gamesListAtom);

  return games.map(item => {
    const config = gameConfigs.find(g => g.pack === item.pack);
    return config ? { ...config, ...item } : item;
  });
});

/** 更新最近游戏原子方法（修复状态合并问题） */
export const updateRecentlyGameAtom = atom(
  null,
  (get, set, newGames: GameItemType[] | ((prev: GameItemType[]) => GameItemType[])) => {
    const currentGames = get(recentlyGameAtom);
    const updatedGames = typeof newGames === 'function' ? newGames(currentGames) : newGames;

    // 过滤白名单游戏并合并字段
    const filtered = updatedGames
      .filter(game => parseInt(game.pack.replace('game', '')) < 100 || recentlyGameWhiteList.includes(game.pack))
      .map(game => ({
        ...game,
        pack: game.pack,
        company: game.company || 'default', // 确保公司字段存在
        img: game.img || getGamesUrl(game.pack)
      }));

    // 使用Map实现严格去重（pack+company组合唯一）
    const uniqueGames = Array.from(
      new Map(filtered.map(game => [`${game.pack}-${game.company}`, game])).values()
    ).slice(0, 3); // 保持最近3个

    set(recentlyGameAtom, uniqueGames);
    Storage.set('PLAYED_GAMES', uniqueGames);
  }
);

/** 异步获取游戏列表 */
export const asyncGamesAtom = atom(null, async (get, set) => {
  try {
    const gameLayout = await request({
      url: '/gamer/get_game_layout',
      method: 'post',
      data: {
        directIn: 1,
        version: 1,
        roomconfigname: import.meta.env.VITE_GAME_ROOM_CONFIG_NAME,
        configname: import.meta.env.VITE_GAME_CONFIG_NAME
      }
    });

    const games: GameItemType[] = [];

    gameLayout.config.forEach(array => {
      array.forEach(item => {
        const { room_list, pack, gamename, h5_game_sort, available } = item;
        if (available) {
          games.push({
            order: h5_game_sort,
            room_list,
            available: available,
            gamename,
            pack,
            img: getGamesUrl(pack)
          });
        }
      });
    });

    const gameJson = await getLabelGames();
    const list = (gameJson?.games || [])
      .filter(item => item.enable)
      .map(item => {
        const targetItem = games.find(game => game.pack === item.pack);
        return {
          ...targetItem,
          ...item
        };
      })
      .sort((a, b) => (a.sort.HOT - b.sort.HOT < 0 ? -1 : 1));

    const availableGames = list.filter(
      item =>
        !/^game\d+$/.test(item.pack) ||
        parseInt(item.pack.replace('game', '')) < 100 ||
        recentlyGameWhiteList.includes(item.pack) ||
        !!item.isCanRecommend
    );
    const storedGames = Storage.get('PLAYED_GAMES') || [];
    const initialRecentGames = [...storedGames, ...availableGames].slice(0, 6);
    set(updateRecentlyGameAtom, initialRecentGames);

    set(gamesListAtom, list);
    set(advertisesAtom, gameJson?.advertise?.map(game => list?.find(item => item.pack === game)) || []);
    set(recommendAtom, gameJson?.recommend?.map(game => list?.find(item => item.pack === game)) || []);
    set(
      christmasAtom,
      gameJson?.christmas
        ?.map(game => list?.find(item => item.pack === game))
        ?.filter(Boolean)
        ?.map(game => ({ ...game, img: `${game.img}?v=christmas` })) || []
    );
    set(
      fishAtom,
      gameJson?.fish
        ?.map(game => list?.find(item => item.pack === game))
        ?.filter(Boolean)
        ?.map(game => ({ ...game, img: `${game.img}?v=20250110` })) || []
    );
  } catch (error) {
    set(gamesListAtom, []);
    set(advertisesAtom, []);
    set(recommendAtom, []);
    set(christmasAtom, []);
    set(fishAtom, []);
  }
});

/** 收藏的游戏列表 */
export const collectGamesAtom = atom<string[]>([]);
/** 异步获取收藏游戏列表 */
export const asyncCollectGamesAtom = atom(null, async (get, set) => {
  const data = await request({
    url: '/gamer/get_collects'
  });
  const collectGames = data?.record?.[0]?.gamelist.split(',') || [];
  set(collectGamesAtom, collectGames);
});
/**
 * 收藏/取消收藏游戏原子函数
 * @param gamePack 游戏包名
 * @param isCollect 是否收藏（true表示收藏，false表示取消）
 * @returns Promise 包含请求结果
 *
 * 使用示例：
 * import { useSetAtom } from 'jotai'
 * const collectGame = useSetAtom(collectGameAtom)
 * // 收藏游戏
 * await collectGame(['slots-777', true])
 * // 取消收藏
 * await collectGame(['poker-king', false])
 *
 * 注意：会自动更新collectGamesAtom的本地状态
 */
export const collectGameAtom = atom(null, (get, set, [gamePack, isCollect]: [string, boolean]) => {
  return request({
    url: '/gamer/to_collect',
    method: 'post',
    data: {
      game: gamePack,
      act: isCollect ? 0 : 1
    }
  }).then(() => {
    const currentCollects = get(collectGamesAtom);
    implog('collect_game', {
      game: gamePack,
      act: isCollect ? 1 : 0
    });
    if (isCollect) {
      // 添加收藏
      set(collectGamesAtom, [...currentCollects, gamePack]);
    } else {
      // 取消收藏
      set(
        collectGamesAtom,
        currentCollects.filter(pack => pack !== gamePack)
      );
    }
  });
});
/** 游戏操作相关API */
export const gameActions = {
  exitGame: () => {
    return request({
      url: '/gamer/exitGame',
      method: 'post'
    });
  },

  entryGame: (data: any) => {
    return request({
      url: '/gamer/getLink',
      method: 'post',
      data
    });
  },

  getGuideGame: (data: any) => {
    return request({
      url: '/gamer/get_guide_game',
      method: 'post',
      data
    });
  }
};
/** 玩家最近游玩的游戏历史列表 */
export const GamesHistoryAtom = atom<string[]>([]);
/** 异步获取玩家最近游玩的游戏历史列表 */
export const asyncGamesHistoryAtom = atom(null, async (get, set) => {
  const userid = Storage.getAccess(ACCESS_USERID_KEY);
  const data = await request({
    url: '/round/get_play_history',
    params: {
      userid
    }
  });
  const gamesHistory = data?.list || [];
  set(GamesHistoryAtom, gamesHistory);
});
/** 获取游戏标签列表 */
export const getLabelGames = () =>
  fetch(`${window.imgPrefix}/game_data/game.json?v=${(window as any).instanceKey}`).then(res => res.json());

/** 获取固定游戏顺序 */
export const getFixedGameOrder = () => fetch(`${window.imgPrefix}/game-order.json`).then(res => res.json());
